import React, { useMemo } from "react";
import { useAtom } from "jotai";
import moment from "moment";
import {
  DocumentsData,
  DocumentsOperations,
} from "constant-data/documentsData";
import {
  docItemsAtom,
  openedDocumentAtom,
  selectedDocumentTypeAtom,
  selectedDocumentTypeTemplatesAtom,
} from "atoms/documentsAtom";
import useDocuments from "features/Documents/useDocuments";
import { useDocReferenceData } from "features/Documents/useDocReferenceData";
import { mapDocItemsToDraw } from "features/Documents/docItemsToDraw";

import { Button } from "components/Button";
import {
  ModalShell,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useModal,
  SelectModal,
} from "components/Modal";

import { DocumentHeaderFields } from "./DocumentHeaderFields";
import { DocItemsTable } from "./DocItemsTable";
import { printDocumentByTemplate } from "./printExcel";
import styles from "./styles.module.scss";

/** Модалка документа: шапка + позиции + сохранение/печать/дублирование */
export const DocumentModal = () => {
  const { updateDocument, duplicateDocument, closeDocumentModal } =
    useDocuments();
  const { openModal2, closeModal2 } = useModal();
  const { storesOptions } = useDocReferenceData();

  const [openedDocument] = useAtom(openedDocumentAtom);
  const [selectedDocumentType] = useAtom(selectedDocumentTypeAtom);
  const [templates] = useAtom(selectedDocumentTypeTemplatesAtom);
  const [docItems] = useAtom(docItemsAtom);

  const modalTitle = useMemo(() => {
    if (!openedDocument) return "";
    const operationName = DocumentsOperations.find(
      (op) => Number(op.id) === Number(openedDocument.type)
    )?.name?.toLowerCase();
    return `Документ ${operationName} товаров №${openedDocument.docid} от ${moment(
      openedDocument.time
    ).format("DD.MM.YYYY HH:mm:ss")}`;
  }, [openedDocument]);

  const config = openedDocument?.type
    ? DocumentsData[String(openedDocument.type)]
    : null;

  if (!config || !selectedDocumentType) return null;

  const onDuplicateClick = () => {
    openModal2(
      <SelectModal
        title="Выберите тип нового документа"
        options={DocumentsOperations.filter((op) => op.include).map((op) => ({
          value: op.id,
          label: op.name,
        }))}
        onSelect={duplicateDocument}
        onClose={closeModal2}
      />
    );
  };

  const onPrintClick = () => {
    openModal2(
      <SelectModal
        title="Выберите шаблон для печати"
        buttonText="Напечатать"
        options={(templates || []).map((t) => ({
          value: String(t.template_id),
          label: t.title,
          file: t.file_path,
        }))}
        onSelect={(option) => {
          if (!option?.file) return;
          closeModal2();
          printDocumentByTemplate({
            fileUrl: option.file,
            document: openedDocument,
            docItems: mapDocItemsToDraw(docItems),
            fileName: `fp-document-${selectedDocumentType.id}.xlsx`,
          });
        }}
        onClose={closeModal2}
      />
    );
  };

  return (
    <ModalShell className={styles.modal}>
      <ModalHeader title={modalTitle} onClose={closeDocumentModal} />
      <ModalBody>
        <DocumentHeaderFields
          elements={config.modalData}
          storesOptions={storesOptions}
        />
        <DocItemsTable
          columns={config.modalColumns}
          bottomRow={config.modalBottomRow}
        />
      </ModalBody>
      <ModalFooter align="start">
        <Button
          icon="galochka"
          variant="secondary"
          onClick={() => updateDocument(false)}
        >
          Сохранить
        </Button>
        <Button icon="tickBlack" onClick={() => updateDocument(true)}>
          Ок
        </Button>
        <Button onClick={closeDocumentModal}>Закрыть</Button>
        <Button icon="print" onClick={onPrintClick}>
          Печать
        </Button>
        <Button icon="download" onClick={onDuplicateClick}>
          Ввести на основании
        </Button>
      </ModalFooter>
    </ModalShell>
  );
};
